import { Injectable } from '@angular/core';
import { CanMatch, Route, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Config } from '@other/interfaces';
import { CognosService } from '@services/cognos.service';
import { ConfigState } from '@store/config.state';
import { LogService } from '@services/log.service';

@Injectable()
export class CapabilityAccess implements CanMatch {

  @SelectSnapshot(ConfigState) config !: Config;

  constructor (
    private _cognos: CognosService,
    private logger: LogService,
    private router: Router
  ) {}

  // CanMatch replaces both CanLoad and CanActivate for this use case
  canMatch(route: Route, segments: UrlSegment[]): boolean | UrlTree {
    this.logger.info("CapabilityAccess","canMatch", "canMatch")
    this.logger.info("CapabilityAccess","canMatch", "route: "+JSON.stringify(route))
    this.logger.info("CapabilityAccess","canMatch", "segments: "+JSON.stringify(segments))
    return this.checkAccess(route.path);
  }

  private checkAccess(path: string | undefined): boolean | UrlTree {
    if (!path) {
      this.logger.info("CapabilityAccess","checkAccess", "path is undefined. Access Denied.")
      return false;
    }
    if (!this.config.corpintra) {
      this.logger.info("CapabilityAccess","checkAccess", "corpintra is false. Access Granted.")
      return true; // Give access to any on non Cognos Server 
    }

    this.logger.info("CapabilityAccess","checkAccess", "access.guard")
    this.logger.info("CapabilityAccess","checkAccess", "path: "+path)
    
    const currentRoute = path.replace(/\-/g, '_');
    let isDenied = false;

    // -------------------------------------------------------------
    // TEST OVERRIDE: Check LocalStorage to FORCE DENY access
    // Usage: localStorage.setItem('deny_order_intake', 'true')
    // -------------------------------------------------------------
    const denyKey = `co_deny_${currentRoute}`;
    if (localStorage.getItem(denyKey) === 'true') {
      this.logger.info("CapabilityAccess", "checkAccess", `Access DENIED by localStorage [${denyKey}]`);
      isDenied = true;
    } else {
      // -------------------------------------------------------------
      // Retrieve current target
      const target = this.config.target;
      if (this.config.debug) console.log('Target:', target) // Debug
      if (this.config.debug && this._cognos.userCapabilities.getValue().admin) console.log('Conf:', this._cognos.userCapabilities.getValue()[target])
      
      let access = false;
      try {
        // Retrieve current route access from user capabilities
        access = this._cognos.userCapabilities.getValue()[target][currentRoute];
      } catch (err) { 
        this.logger.info("CapabilityAccess","checkAccess", "Error: "+err)
      }
      this.logger.info("CapabilityAccess","checkAccess", "Decision: "+access) // Debug
      
      if (!access) isDenied = true;
    } 

    if (isDenied) {
      this.logger.info("CapabilityAccess", "checkAccess", "Access Denied. Rotating to next available route.");
      
      // Find the current route index in the router configuration
      const config = this.router.config;
      const currentIndex = config.findIndex(r => r.path === path);
      
      // Iterate through subsequent routes to find the next valid one
      for (let i = currentIndex + 1; i < config.length; i++) {
        const nextRoute = config[i];
        // We skip:
        // - Routes without paths
        // - The wildcard route '**' (we handle fallback explicitly)
        // - Redirects (like the root '')
        if (nextRoute.path && nextRoute.path !== '**' && !nextRoute.redirectTo) {
          // Use createUrlTree instead of parseUrl for proper absolute redirect
          this.logger.info("CapabilityAccess", "checkAccess", "Redirecting to next potential route: /" + nextRoute.path);
          return this.router.createUrlTree(['/' + nextRoute.path], { queryParamsHandling: 'preserve' });
        }
      }
      
      // If we ran out of routes, fallback to help
      this.logger.info("CapabilityAccess", "checkAccess", "No more routes available. Redirecting to help.");
      return this.router.createUrlTree(['/help'], { queryParamsHandling: 'preserve' });
    }

    return true;
  }
}
